import { Box, Grid } from '@material-ui/core';
import { Button, Dialog, Icons, Typography } from '@nutrien/cxp-components';
import * as markerjs2 from 'markerjs2';
import React, { useEffect, useState } from 'react';

import { AimsFile } from '../../models/models';
import { FILES_IMAGE_TYPE, IMAGE_PATH } from '../../utilities/constants';
import { getFileExtension } from '../../utilities/utilityFunctions';
import { useStyles } from './FileSelectionViewThumbnail.styles';
const { MARKER_JS_2_KEY } = process.env;

interface Props {
  fileData: AimsFile;
  index: number;
  fileList: AimsFile[];
  updateFileList: (updatedFileList: AimsFile[]) => void;
  onDelete: (fileData: AimsFile) => void;
  uniqueId: string;
}

const Thumbnail: React.FC<Props> = ({
  fileData,
  index,
  fileList,
  updateFileList,
  onDelete,
  uniqueId,
}: Props) => {
  const classes = useStyles();
  const [fileUrl, setImageURL] = useState<string>(fileData ? URL.createObjectURL(fileData) : '');
  const [imageName, setImageName] = useState<string>(fileData.name);
  const isImage = FILES_IMAGE_TYPE.some(item => fileData.type?.toLowerCase().includes(item));
  const id = uniqueId;
  const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
  const imgRef = React.createRef<HTMLImageElement>();
  const [hideImage, setHideImage] = useState<boolean>(true);

  useEffect(() => {
    setImageURL(fileData ? URL.createObjectURL(fileData) : '');
    setImageName(fileData ? fileData.name : imageName);
  }, [fileData, imageName]);

  const getSvgPath = () => {
    if (imageName?.toLowerCase().endsWith('.pdf')) return `pdf128_128.svg`;
    if (imageName?.toLowerCase().endsWith('.mp4')) return 'mp4128_128.svg';
    if (imageName?.toLowerCase().endsWith('.mp3')) return 'mp3128_128.svg';
    if (imageName?.toLowerCase().endsWith('.txt')) return 'txt128_128.svg';
    if (imageName?.toLowerCase().endsWith('.csv')) return 'csv128_128.svg';
    if (imageName?.toLowerCase().endsWith('.xlsx') || imageName?.toLowerCase().endsWith('.xls'))
      return 'xls128_128.svg';
    if (imageName?.toLowerCase().endsWith('.ppt') || imageName?.toLowerCase().endsWith('.pptx'))
      return 'ppt128_128.svg';
    if (imageName?.toLowerCase().endsWith('.docx') || imageName?.toLowerCase().endsWith('.doc'))
      return 'doc128_128.svg';
    if (imageName?.toLowerCase().endsWith('.mov')) return 'mov.svg';
  };

  const handleDelete = (fileToDelete: AimsFile) => {
    if (onDelete && fileToDelete) onDelete(fileToDelete);
    setDeleteConfirm(false);
  };

  const editImageFile = async (dataURL: string, fileName: string): Promise<File> => {
    const blobUrl = await (await fetch(dataURL)).blob();
    const updatedBlobFile: File = new File([blobUrl], `${fileName}`, {
      type: 'image/jpeg',
    });
    return updatedBlobFile;
  };

  const changeImageUrl = async (val: string) => {
    let fileName = fileData.updatedName;
    let ext = getFileExtension(fileName);
    if (ext === undefined) {
      ext = getFileExtension(fileData.name);
      fileName = fileName + '.' + ext;
    }
    const editedBlobFile = await editImageFile(val, fileName);
    const updatedFileList: AimsFile[] = fileList.slice();
    const updatedFile: AimsFile = Object.assign(editedBlobFile, {
      updatedName: editedBlobFile.name,
      fileDescription: fileData.fileDescription,
      showOnReport: fileData.showOnReport,
    });
    updatedFileList[index] = updatedFile;
    updateFileList(updatedFileList);
  };

  const showMarkerArea = () => {
    if (imgRef.current !== null) {
      const markerArea = new markerjs2.MarkerArea(imgRef.current);
      markerArea.settings.displayMode = 'popup';
      markerArea.uiStyleSettings.toolbarHeight = 60;
      markerArea.uiStyleSettings.canvasBackgroundColor = '#111111';
      markerArea.uiStyleSettings.toolbarActiveButtonStyleColorsClassName = classes.activeButton;
      markerArea.uiStyleSettings.toolboxBackgroundColor = 'black';
      if (MARKER_JS_2_KEY && !markerjs2.Activator.isLicensed) {
        markerjs2.Activator.addKey(MARKER_JS_2_KEY);
      }
      const overlayImage: HTMLElement | null = document.getElementById('markerOverlaySelect');
      if (overlayImage) {
        markerArea.targetRoot = overlayImage;
      }
      markerArea.settings.defaultColorSet = ['#e8e8e8', '#cd4f5f', '#ffd966', '#226ea6'];
      markerArea.addRenderEventListener(dataUrl => {
        setHideImage(true);
        if (imgRef.current) {
          imgRef.current.src = dataUrl;
          changeImageUrl(dataUrl);
        }
        changeImageUrl(dataUrl);
      });
      markerArea.addCloseEventListener(() => {
        setHideImage(true);
      });
      markerArea.show();
      setHideImage(false);
    }
  };

  useEffect(() => {
    if (isImage) {
      const element = document.getElementById(id);
      if (element) {
        element.style.background = `url("${fileUrl}") center`;
      }
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.style.background = `url(${IMAGE_PATH}/${getSvgPath()})`;
        element.style.backgroundSize = `128px 128px`;
        element.style.backgroundRepeat = 'no-repeat';
        element.style.backgroundPosition = 'center';
      }
    }
  }, [fileUrl]);

  return (
    <>
      <Grid>
        <img
          ref={imgRef}
          src={fileUrl}
          id={`editImg${fileData.name}`}
          className={classes.imgGrid}
          hidden={hideImage}
          onClick={showMarkerArea}
        />
        <div id="markerOverlaySelect" />
      </Grid>

      {isImage ? (
        <Box className={classes.thumbnail} id={id} onClick={() => {}}>
          <div className={classes.div1}>
            <div className={classes.div2}>
              <div
                onClick={() => document.getElementById(`editImg${fileData.name}`)?.click()}
                className={classes.editIcon}
                data-testid={`edit-${fileData.name}`}
              >
                <Icons.EditFeather color="primary" />
              </div>

              <div
                onClick={() => setDeleteConfirm(true)}
                className={classes.trashIcon}
                data-testid={`delete-${fileData.name}`}
              >
                <Icons.Trash2Feather color="error" />
              </div>
            </div>
          </div>
        </Box>
      ) : (
        <Grid className={classes.thumbnailForSvg} id={id}>
          <div className={classes.div1}>
            <div className={classes.div2}>
              <div
                onClick={() => setDeleteConfirm(true)}
                className={classes.trashIcon}
                data-testid={`delete-${fileData.name}`}
              >
                <Icons.Trash2Feather color="error" />
              </div>
            </div>
          </div>
        </Grid>
      )}

      {deleteConfirm && (
        <Dialog
          onClose={() => setDeleteConfirm(false)}
          open={deleteConfirm}
          titleText="Delete File"
          backButton={false}
          dialogActions={
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              className={classes.deletebuttonGrid}
            >
              <Grid item>
                <Button
                  noMinHeight
                  color="error"
                  variant="contained"
                  onClick={() => handleDelete(fileData)}
                  data-testid={`confirm-delete-${fileData.name}`}
                >
                  Yes, Delete
                </Button>
              </Grid>
            </Grid>
          }
          maxWidth="xs"
          noMinHeight
        >
          <Grid container className={classes.dialogGrid}>
            <Grid item container xs={12} alignItems="center">
              <Typography className={classes.deleteFile}>
                {`Are you sure want to delete "${fileData.name}"?`}
              </Typography>
            </Grid>
          </Grid>
        </Dialog>
      )}
    </>
  );
};

export default Thumbnail;
